<template>
    <div class="video-container">
        <video
            :class="props.preview ? 'max-h-20 w-auto' : ( props.pane ? 'h-40 w-80 object-cover' : 'max-h-80 w-auto')"
            :controls="!props.preview && !props.pane"
            class="rounded-sm video-thumbnail"
            preload="metadata"
            @click="openLightbox"
        >
            <source :src="url">
        </video>

        <div v-if="progress !== undefined && progress < 100 && progress > 0"
             class="loading-overlay">
            <div class="loading-content">
                <div class="spinner"></div>
                <span class="progress-text">{{ progress }}%</span>
            </div>
        </div>
    </div>
    <div v-if="caption && !props.pane" class="mt-4 text-sm text-gray-600 dark:text-gray-50" v-html="caption"></div>

    <Teleport to="body">
        <div v-if="isOpen" class="lightbox-overlay" @click.self="closeLightbox">
            <div class="top-controls">
                <button class="control-btn" title="Baixar" @click="download">
                    <svg fill="none" height="20" stroke="currentColor" stroke-linecap="round"
                         stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" width="20">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" x2="12" y1="15" y2="3"/>
                    </svg>
                </button>
                <button class="control-btn" title="Fechar" @click="closeLightbox">
                    <svg fill="none" height="24" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                         stroke-width="2" viewBox="0 0 24 24" width="24">
                        <line x1="18" x2="6" y1="6" y2="18"></line>
                        <line x1="6" x2="18" y1="6" y2="18"></line>
                    </svg>
                </button>
            </div>

            <div class="lightbox-content">
                <video
                    :src="url"
                    class="lightbox-video"
                    controls
                >
                </video>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from "vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useMessageStore} from "@src/stores/messages";
import ChatMessage from "@src/types/ChatMessage";
import {downloadFile} from "@src/helpers/utils";

const props = defineProps(['data', 'preview', 'pane', 'id'])
const url = ref<string>('')
const caption = ref<string>('')
const isOpen = ref<boolean>(false)
const sessionStore = useChatSessionStore()

const progress = computed(() => {
    const messageStore = useMessageStore()
    const message = sessionStore.type === 'session'
        ? sessionStore.selectedSession?.messages?.find((msg: ChatMessage) => msg.id === props.id)
        : messageStore.groupMessages?.find(msg => msg.id === props.id);

    return message?.progress || 0;
})
const download = () => downloadFile(url.value)

const openLightbox = () => {
    if (props.preview) {
        return
    }
    if (url.value) {
        isOpen.value = true
        document.body.style.overflow = 'hidden'
    }
}

const closeLightbox = () => {
    isOpen.value = false
    document.body.style.overflow = ''
}

onMounted(() => {
    try {
        const parsed = JSON.parse(props.data)
        url.value = parsed.url
        if (parsed.caption !== parsed.url) {
            caption.value = parsed.caption
        }
    } catch (e) {
        url.value = props.data
    }
})

</script>

<style scoped>
.video-container {
    position: relative;
    display: inline-block;
}

.video-thumbnail {
    cursor: pointer;
    display: block;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    border-radius: inherit;
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: white;
}

.spinner {
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid #fff;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
    margin-bottom: 5px;
}

.progress-text {
    font-weight: bold;
    font-size: 0.9rem;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

/* Lightbox Styles */
.lightbox-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
}

.top-controls {
    position: fixed;
    top: 20px;
    right: 20px;
    display: flex;
    gap: 8px;
    z-index: 10000;
}

.control-btn {
    border: none;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(4px);
    border-radius: 50%;
    width: 44px;
    height: 44px;
    cursor: pointer;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, transform 0.2s ease;
}

.control-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
}

.lightbox-content {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
}

.lightbox-video {
    max-width: 90%;
    max-height: 90%;
    cursor: pointer;
}
</style>